import { message } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { UserIdentity } from '@/components/RightProvider/const';
import { RightContext } from '@/components/RightProvider/context';
import { LimitConfig } from '@/components/RightProvider/type';
import { UseTranslateType } from '@/hooks/useTranslate';
import PaymentStore, {
  DateType,
  getAvailableCurrencies,
  HighlightBenefit,
  OrderType,
} from '@/store/Payment';
import {
  executePayment,
  executePolling,
  getApplicableCoupon,
} from '@/store/Payment/paymentStrategy';
import UserStore, { DistributionSpecificCouponInfo, IDENTITYKEY } from '@/store/User';
import { AIFunction } from '@/utils/const';
import {
  EventNewID,
  fc_purchase_alert_expose,
  GoogleEventID,
  StatusEnum,
  SubscribeReasonEnum,
} from '@/utils/eventlog/eventNewConfig';
import { trackEvent, trackGoogleEvent } from '@/utils/eventlog/logAdapter';

import { FWebsiteEmail, getPlanTag } from '../utils';

interface UsePaymentProps {
  t: UseTranslateType | undefined;
  defaultActiveTab?: DateType;
  feature: fc_purchase_alert_expose['from'];
  reason?: SubscribeReasonEnum;
}

interface HandlePayProps {
  key: UserIdentity;
  item: LimitConfig[keyof LimitConfig];
  feature: fc_purchase_alert_expose['from'];
  btnIndex: number;
  price: string;
  currency: string;
  selectedCommodityCode?: string;
}

// 计算最大折扣
function getMaxDisCount(limitConfig: LimitConfig): HighlightBenefit | false {
  const getMaxPrice = (data: any[]): number => {
    if (!data || !Array.isArray(data) || data.length === 0) return 0;
    return Math.max(...data.map((item) => item?.realPrice || 0));
  };

  const calculateDiscount = (prices: any): number => {
    const { month, year } = prices?.commodityData || {};
    const monthPrice = getMaxPrice(month);
    const yearPrice = getMaxPrice(year);
    return monthPrice ? Math.floor((1 - yearPrice / 12 / monthPrice) * 100) : 0;
  };

  try {
    if (!limitConfig.advancedVip) {
      return false;
    }
    const advancedDiscount = limitConfig.advancedVip
      ? calculateDiscount(limitConfig.advancedVip)
      : 0;
    return {
      type: 'advancedVip' as IDENTITYKEY,
      disCount: advancedDiscount,
    };
  } catch (error) {
    return false;
  }
}

// 企业版特殊处理
function handleEnterpriseCase() {
  window.location.href = 'mailto:' + FWebsiteEmail;
}

const usePayment = ({ t, defaultActiveTab, feature, reason }: UsePaymentProps) => {
  // 从 Store 获取状态和方法
  const {
    paymentMethod,
    activeTab,
    setActiveTab,
    isPurchaseSuccess,
    setIsPurchaseSuccess,
    highlightBenefit,
    setHighlightBenefit,
    loadingStates,
    setBtnLoading,
    initLoadingStates,
    setOrderInfo,
    orderInfo,
    setOrderCouponCode,
    activeCombo,
    setActiveCombo,
    selectedCurrency,
    resetPaymentState,
  } = PaymentStore();

  // 从 Context 获取配置
  const { limitConfig, currentVip, withUpdate } = useContext(RightContext);

  // 优惠券信息
  const couponInfo = UserStore((store) => store.couponInfo);

  // 埋点用 ref
  const featureRef = useRef<fc_purchase_alert_expose['from']>(feature);
  const reasonRef = useRef<SubscribeReasonEnum | undefined>(reason);
  const activeTabRef = useRef<DateType>(activeTab);
  const activeComboRef = useRef<IDENTITYKEY>(activeCombo);

  // 轮询定时器
  const timer = useRef<NodeJS.Timeout | null>(null);
  const pollPayKey = 'pollPayKey';

  // 二维码弹窗销毁处理
  const QRCodeDestroyHandler = useRef<() => void>();

  // 格式化数据
  const formatData = useMemo(() => {
    return Object.entries(limitConfig).sort(
      ([, a], [, b]) => ((a as any).weight || 0) - ((b as any).weight || 0),
    ) as [keyof LimitConfig, LimitConfig[keyof LimitConfig]][];
  }, [limitConfig]);

  const weightData = useMemo(() => {
    return Object.values(limitConfig).map((item) => item.weight);
  }, [limitConfig]);

  // 获取当前选中周期的所有商品可用货币
  const availableCurrencies = useMemo(() => {
    const allCommodities: any[] = [];
    formatData.forEach(([, config]) => {
      const commodities = config.commodityData?.[activeTab as DateType] || [];
      allCommodities.push(...commodities);
    });
    return getAvailableCurrencies(allCommodities);
  }, [formatData, activeTab]);

  // 初始化
  useEffect(() => {
    if (defaultActiveTab) {
      setActiveTab(defaultActiveTab);
    }
    initLoadingStates(formatData.length);

    return () => {
      timer.current && clearTimeout(timer.current);
      resetPaymentState();
    };
  }, []);

  // 同步 ref
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    activeComboRef.current = activeCombo;
  }, [activeCombo]);

  // 计算高亮套餐
  useEffect(() => {
    const maxDiscount = getMaxDisCount(limitConfig);
    if (maxDiscount) {
      setHighlightBenefit(maxDiscount);
    }
  }, [limitConfig]);

  // 优惠券自动切换 Tab
  useEffect(() => {
    if (!limitConfig) return;

    const ticketHandle = couponInfo.type === 'ticket' && couponInfo.availabled;
    const specificHandle =
      couponInfo.type === 'distribution_specific' &&
      (couponInfo as DistributionSpecificCouponInfo)?.data?.commodityDiscount?.length;

    if (!ticketHandle && !specificHandle) return;

    const applicableGoods = ticketHandle
      ? couponInfo.availabled?.map((item) => item.applicableGoods).flat()
      : (couponInfo as DistributionSpecificCouponInfo)?.data?.commodityDiscount?.map(
          (item) => item.commodityCode,
        );

    if (!applicableGoods?.length) return;

    // 查找匹配的 Tab
    for (const vip of Object.keys(limitConfig)) {
      const config = limitConfig[vip as UserIdentity];
      const periods: DateType[] = ['month', 'year', 'twoweek'];

      for (const period of periods) {
        const commodities = config?.commodityData?.[period] || [];
        const hasMatch = commodities.some((item: any) =>
          applicableGoods.includes(item.commodityCode),
        );

        if (hasMatch) {
          setActiveTab(period);
          return;
        }
      }
    }
  }, [limitConfig, couponInfo]);

  // 支付成功回调
  const handlePaymentSuccess = useCallback(
    async (orderId: string) => {
      const skuDataArray =
        limitConfig?.[activeComboRef.current]?.commodityData?.[
          activeTabRef.current as DateType
        ];
      const skuData =
        skuDataArray?.find(
          (item: any) => item.commodityCode === orderInfo.orderCommodityCode,
        ) || skuDataArray?.[0];
      const plan = getPlanTag(activeComboRef.current, activeTabRef.current);
      const purchaseItemId = orderInfo.orderCommodityCode || skuData?.commodityCode;
      const purchaseItems = purchaseItemId
        ? [
            {
              item_id: purchaseItemId,
              item_name: skuData?.content?.commodityName || plan,
              price: skuData?.realPrice,
              quantity: 1,
            },
          ]
        : undefined;

      setIsPurchaseSuccess(true);

      // Google 埋点
      trackGoogleEvent(GoogleEventID.Purchase, {
        transaction_id: `payment_${orderId}`,
        value: skuData?.realPrice,
        currency: skuData?.content?.currency,
        subscription_plan: plan,
        items: purchaseItems,
      });

      // 自定义埋点
      trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
        source: featureRef.current,
        plan: getPlanTag(activeComboRef.current, activeTabRef.current),
        result: StatusEnum.SUCCESS,
        reason: reasonRef.current,
      });

      message.open({
        key: pollPayKey,
        type: 'success',
        content: t && t('merror.sub.purchasesuc'),
        duration: 2,
      });

      // 关闭二维码弹窗
      QRCodeDestroyHandler.current?.();
      setOrderCouponCode('');

      localStorage.setItem('userPurchaseFlag', 'true');
      await withUpdate(() => {
        UserStore.getState().setShowNewUserCountDownBaner(false);
      }, false);
    },
    [limitConfig, orderInfo, t, withUpdate],
  );

  // 支付失败回调
  const handlePaymentError = useCallback(
    (msg: 'timeout' | string) => {
      trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
        source: featureRef.current,
        plan: getPlanTag(activeComboRef.current, activeTabRef.current),
        result: StatusEnum.FAIL,
        reason: reasonRef.current,
      });

      if (msg === 'timeout') {
        message.open({
          key: pollPayKey,
          type: 'error',
          content: t && t('merror.sub.longtime'),
          duration: 2,
        });
      } else {
        message.open({
          key: pollPayKey,
          type: 'error',
          content: msg || (t && t('merror.sub.purchasefail')),
          duration: 2,
        });
      }
    },
    [t],
  );

  // 处理 Tab 切换
  const handleTabChange = useCallback((tab: DateType) => {
    setActiveTab(tab);
  }, []);

  // 主支付方法
  const handlePay = useCallback(
    async ({
      key,
      item,
      btnIndex,
      feature = AIFunction.Other,
      selectedCommodityCode = '',
    }: HandlePayProps) => {
      // 防止重复点击
      if (loadingStates.some((state) => state)) return;

      setIsPurchaseSuccess(false);
      featureRef.current = feature;
      setActiveCombo(key);
      activeComboRef.current = key;

      // 企业版特殊处理
      if (key === 'enterpriseVip') {
        handleEnterpriseCase();
        return;
      }

      const plan = getPlanTag(key, activeTabRef.current);
      const commodityDataArray = item.commodityData[activeTab as DateType] || [];
      const selectedCommodity = selectedCommodityCode
        ? commodityDataArray.find(
            (commodity: any) => commodity.commodityCode === selectedCommodityCode,
          )
        : commodityDataArray[0];
      const beginCheckoutValue = selectedCommodity?.realPrice;
      const beginCheckoutCurrency = selectedCommodity?.content?.currency;
      const beginCheckoutItemId =
        selectedCommodity?.commodityCode || selectedCommodityCode;
      const beginCheckoutItems = beginCheckoutItemId
        ? [
            {
              item_id: beginCheckoutItemId,
              item_name: selectedCommodity?.content?.commodityName || plan,
              price: beginCheckoutValue,
              quantity: 1,
            },
          ]
        : undefined;

      // 埋点：开始结账
      trackGoogleEvent(GoogleEventID.BeginCheckout, {
        subscription_plan: plan,
        value: beginCheckoutValue,
        currency: beginCheckoutCurrency,
        items: beginCheckoutItems,
      });
      trackEvent(EventNewID.SUBSCRIPTION_POP_CLICK, {
        source: feature,
        plan,
        reason: reasonRef.current,
      });

      // 检查商品数据
      if (!item.commodityData?.month?.length && !item.commodityData?.year?.length) {
        return;
      }

      // 检查用户等级限制
      if (currentVip.key === 'freeVip' && key === 'freeVip') {
        return;
      }

      // 获取商品数据
      // 确定商品编码
      let commodityCode = '';
      if (selectedCommodityCode && Array.isArray(commodityDataArray)) {
        if (
          commodityDataArray.some(
            (item: any) => item.commodityCode === selectedCommodityCode,
          )
        ) {
          commodityCode = selectedCommodityCode;
        }
      }
      if (
        !commodityCode &&
        Array.isArray(commodityDataArray) &&
        commodityDataArray.length > 0
      ) {
        commodityCode = commodityDataArray[0].commodityCode;
      }

      if (!commodityCode) {
        console.error('商品code不存在');
        return;
      }

      const shouldUseSinglePay = paymentMethod === 'stripeAlipay';
      const currentOrderType = shouldUseSinglePay ? OrderType.Once : OrderType.Combo;

      // 更新订单信息
      setOrderInfo({
        orderId: '',
        orderType: currentOrderType,
        orderCommodityCode: commodityCode,
      });

      // 获取优惠券
      const couponCode = getApplicableCoupon(commodityCode);
      if (couponCode) {
        setOrderCouponCode(couponCode);
      }

      try {
        setBtnLoading(btnIndex, true);
        await executePayment(
          {
            feature,
            commodityCode,
            couponCode,
            currency: selectedCurrency,
            orderType: currentOrderType,
          },
          {
            onSuccess: (orderId: string) => {
              setOrderInfo({ orderId });
              message.open({
                key: pollPayKey,
                type: 'loading',
                content: t && t('merror.sub.purchasing'),
                duration: 0,
              });
              // 开始轮询
              executePolling({
                orderInfo: {
                  orderId,
                  orderType: currentOrderType,
                  orderCommodityCode: commodityCode,
                },
                t,
                pollPayKey,
                timer,
                callback: {
                  onSuccess: () => handlePaymentSuccess(orderId),
                  onError: handlePaymentError,
                },
              });
            },
            onError: handlePaymentError,
          },
        );

        setBtnLoading(btnIndex, false);
      } catch (error) {
        message.open({
          key: pollPayKey,
          type: 'error',
          content: (error as any).message || (t && t('merror.sub.purchasefail')),
          duration: 2,
        });
        setBtnLoading(btnIndex, false);
      }
    },
    [
      loadingStates,
      activeTab,
      currentVip,
      selectedCurrency,
      t,
      handlePaymentSuccess,
      handlePaymentError,
      paymentMethod,
    ],
  );

  // 监听页面可见性变化（用于 Stripe 支付回调）
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const currentOrderInfo = PaymentStore.getState().orderInfo;
        if (!currentOrderInfo.orderId || !currentOrderInfo.orderType) {
          return;
        }

        try {
          await executePolling({
            orderInfo: currentOrderInfo,
            t,
            pollPayKey,
            timer,
            callback: {
              onSuccess: () => handlePaymentSuccess(currentOrderInfo.orderId),
              onError: handlePaymentError,
            },
          });
          await withUpdate(() => {}, false);
        } catch (error) {
          console.error(error);
        }
      } else {
        timer.current && clearTimeout(timer.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [t, handlePaymentSuccess, handlePaymentError, withUpdate]);

  return {
    // 状态
    activeTab,
    isPurchaseSuccess,
    highlightBenefit,
    loadingStates,
    formatData,
    weightData,
    availableCurrencies,
    // 方法
    handlePay,
    handleTabChange,
  };
};

export default usePayment;
